import { api } from '@data/store';
import {
  BudgetSheetLink,
  BudgetSheetPayload,
  ProductEntity,
  ReportEntity,
  ProductManagerCandidate,
  ProductParams,
  PmProductsParams,
  ListResponse,
  PmInfiniteData,
  PmParams,
  PmProductsInfiniteData,
  CreateProduct,
  UpdateProduct,
  ProductResponseTime,
  ProductCrashFree,
  ProductDependencies,
  ProductSecurity,
  ProductQualityAudit,
  ProductProductivityReport,
  AuditReportKind,
  ProductAuditReport,
  ProductStoreEntry,
  SentryAccess,
  SentryProject,
  AikidoRepository,
  GithubRepositoryListing,
  RndDossier,
} from '@data/utils/types';

export const fetchProducts = (
  organizationId: string,
  params?: ProductParams,
): Promise<{ items: ProductEntity[]; count: number }> =>
  api.get(`/organizations/${organizationId}/products`, { params });

export const fetchPmProducts = async (
  organizationId: string,
  params: PmProductsParams,
): Promise<ListResponse<ProductEntity>> =>
  api.get(`organizations/${organizationId}/pm/products`, {
    params,
  });

export const fetchInfinitePmProducts = async (
  organizationId: string,
  params: PmParams,
  { pageParam },
): Promise<PmProductsInfiniteData> => {
  // Every filter the caller set, with the sprint's own window laid over it. Spelled out as an allowlist
  // once, which silently dropped `unreadOnly`: the Unread tick narrowed the sprints but never reached
  // the request, so each sprint answered with all of its products.
  const window = Object.fromEntries(Object.entries(pageParam ?? {}).filter(([, value]) => value !== undefined));
  // The four the caller reads back describe the sprint, and every call site supplies them — through the
  // page param, or through the params themselves when there is no paging.
  const newParams = { ...params, ...window } as PmParams & PmInfiniteData;

  const data = await api.get(`organizations/${organizationId}/pm/products`, {
    params: newParams,
  });
  return {
    sprintNumber: newParams.sprintNumber,
    sprintId: newParams.sprintId,
    from: newParams.from,
    to: newParams.to,
    items: data.items,
  };
};

export const createProduct = (organizationId: string, values: CreateProduct): Promise<ProductEntity> =>
  api.post(`/organizations/${organizationId}/products`, values);

export const updateProduct = (productId: string, values: UpdateProduct): Promise<ProductEntity> =>
  api.patch(`/products/${productId}`, values);

// The organization is not in the path: the route is scoped by the product, which belongs to one.
export const deleteProduct = (productId: string): Promise<void> => api.delete(`/products/${productId}`);

export const fetchProduct = (productId: string): Promise<ProductEntity> => api.get(`/products/${productId}`);

/** Every entry on the product between two days, ordinary and incident work alike. */
export const fetchProductReports = (productId: string, from: string, to: string): Promise<ListResponse<ReportEntity>> =>
  api.get(`/products/${productId}/reports`, { params: { from, to } });

// Its own route, not a field on the product: the product route answers clients too.
export const fetchRndDossier = (productId: string): Promise<RndDossier> =>
  api.get(`/products/${productId}/rnd-dossier`);

export const updateRndDossier = (productId: string, values: RndDossier): Promise<RndDossier> =>
  api.put(`/products/${productId}/rnd-dossier`, values);

// The pickers on a product's page. Scoped under the product so they reuse its own access rule.
export const fetchSentryProjects = (productId: string): Promise<ListResponse<SentryProject>> =>
  api.get(`/products/${productId}/sentry/projects`);

export const fetchAikidoRepositories = (productId: string): Promise<ListResponse<AikidoRepository>> =>
  api.get(`/products/${productId}/aikido/repositories`);

export const fetchGithubRepositories = (productId: string): Promise<GithubRepositoryListing> =>
  api.get(`/products/${productId}/github/repositories`);

export const fetchProductManagers = (productId: string): Promise<ListResponse<ProductManagerCandidate>> =>
  api.get(`/products/${productId}/managers`);

export const updateProductManagers = (productId: string, membershipIds: string[]): Promise<ProductEntity> =>
  api.put(`/products/${productId}/managers`, { membershipIds });

export const fetchProductResponseTime = (productId: string): Promise<ProductResponseTime> =>
  api.get(`/products/${productId}/response-time`);

export const fetchProductCrashFree = (productId: string): Promise<ProductCrashFree> =>
  api.get(`/products/${productId}/crash-free`);

export const fetchProductDependencies = (productId: string): Promise<ProductDependencies> =>
  api.get(`/products/${productId}/dependencies`);

export const fetchProductSecurity = (productId: string): Promise<ProductSecurity> =>
  api.get(`/products/${productId}/security`);

/**
 * Links a personal Sentry token to a product Panenco does not administer. The API tries it before it
 * stores it and answers with what it can reach, so the dialog can say more than "that worked".
 *
 * `silentStatuses`: every refusal here has something specific to say, and the dialog says it beside the
 * field where the token still sits. The global toast would repeat the same sentence in the corner.
 */
export const linkProductSentryToken = (productId: string, token: string): Promise<SentryAccess> =>
  api.post(`/products/${productId}/sentry/token`, { token }, { silentStatuses: [400] });

export const unlinkProductSentryToken = (productId: string): Promise<void> =>
  api.delete(`/products/${productId}/sentry/token`);

/*
 * `silentStatuses` covers the 503 the API answers when it could not read GitHub: the tab says that
 * itself, in its own words, and a global error toast would put the plumbing in front of a client.
 */
export const fetchProductQualityAudits = (productId: string): Promise<ListResponse<ProductQualityAudit>> =>
  api.get(`/products/${productId}/quality-audits`, { silentStatuses: [503] });

/** `compareTo`, an earlier quarter such as `2026-Q2`, makes the API add what changed since it. */
export const fetchProductQualityAudit = (
  productId: string,
  year: number,
  quarter: number,
  compareTo?: string,
): Promise<ProductQualityAudit> =>
  api.get(`/products/${productId}/quality-audits/${year}/${quarter}`, {
    // A failed comparison is reported inline beside its picker, so its 404 raises no toast either.
    ...(compareTo ? { params: { compareTo }, silentStatuses: [503, 404] } : { silentStatuses: [503] }),
  });

export const fetchProductProductivityReports = (productId: string): Promise<ListResponse<ProductProductivityReport>> =>
  api.get(`/products/${productId}/productivity-reports`, { silentStatuses: [503] });

export const fetchProductProductivityReport = (
  productId: string,
  year: number,
  quarter: number,
): Promise<ProductProductivityReport> =>
  api.get(`/products/${productId}/productivity-reports/${year}/${quarter}`, { silentStatuses: [503] });

export const fetchProductAuditReports = (productId: string): Promise<ListResponse<ProductAuditReport>> =>
  api.get(`/products/${productId}/audit-reports`, { silentStatuses: [503] });

export const fetchProductAuditReport = (productId: string, kind: AuditReportKind): Promise<ProductAuditReport> =>
  api.get(`/products/${productId}/audit-reports/${kind}`, { silentStatuses: [503] });

/*
 * The same three pickers, asked of the organization rather than of a product.
 *
 * A product being added has no id yet, so the product-scoped routes above cannot fill its pickers —
 * and they never needed one: each lists what the organization's own credentials expose.
 */
export const fetchOrganizationSentryProjects = (organizationId: string): Promise<ListResponse<SentryProject>> =>
  api.get(`/organizations/${organizationId}/sentry/projects`);

export const fetchOrganizationAikidoRepositories = (organizationId: string): Promise<ListResponse<AikidoRepository>> =>
  api.get(`/organizations/${organizationId}/aikido/repositories`);

export const fetchOrganizationGithubRepositories = (organizationId: string): Promise<GithubRepositoryListing> =>
  api.get(`/organizations/${organizationId}/github/repositories`);

/** The figures go up in the request: the Budget tab is the only thing that knows them. */
export const fetchBudgetSheet = (productId: string, year: number): Promise<BudgetSheetLink | null> =>
  // Silent: without it a product nobody has synced toasts a bare "Error" on every visit.
  api.get(`/products/${productId}/budget-sheet`, { params: { year }, silentStatuses: [403, 404] });

export const syncBudgetSheet = (productId: string, payload: BudgetSheetPayload): Promise<BudgetSheetLink> =>
  api.post(`/products/${productId}/budget-sheet`, payload);

export const fetchProductStore = (productId: string): Promise<ListResponse<ProductStoreEntry>> =>
  api.get(`/products/${productId}/store`);

export const writeProductStore = (
  productId: string,
  body: { entries: { key: string; value: string; since?: string }[]; removed?: { key: string; since?: string }[] },
): Promise<ListResponse<ProductStoreEntry>> => api.put(`/products/${productId}/store`, body, { silentSuccess: true });
